import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  AlertTriangle,
  Brain,
  ChevronDown,
  ChevronUp,
  Loader2,
  Mic,
  RotateCcw,
  Send,
  Sparkles,
  X,
} from 'lucide-react';
import {
  getTutorResponse,
  probeTutorHealth,
  TutorError,
  TutorErrorDetails,
  TutorHealth,
  Message as TutorMessage,
} from '../services/aiTutorService';
import { ADVERSYN_KNOWLEDGE_VERSION } from '../data/adversynKnowledge';
import { cn } from '../lib/utils';
import { useSpeechRecognition } from '../hooks/useSpeechRecognition';
import ReactMarkdown from 'react-markdown';
import { safeFormatTimestamp } from '../lib/accountFormatters';

interface AITutorProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ChatMessage {
  role: 'user' | 'assistant' | 'error';
  content: string;
  error?: TutorErrorDetails;
  // For retry: which user prompt produced this error message.
  promptIndex?: number;
}

function asTutorMessages(messages: ChatMessage[]): TutorMessage[] {
  return messages
    .filter((m): m is ChatMessage & { role: 'user' | 'assistant' } =>
      m.role === 'user' || m.role === 'assistant'
    )
    .map((m) => ({ role: m.role, content: m.content }));
}

function HealthPill({ health }: { health: TutorHealth | null }) {
  if (!health) {
    return (
      <div className="flex items-center gap-1.5" title="Probing AI Tutor provider...">
        <div className="w-1.5 h-1.5 rounded-full bg-zinc-400 animate-pulse" />
        <span
          data-testid="tutor-health-status"
          className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest"
        >
          Checking...
        </span>
      </div>
    );
  }
  if (health.status === 'ok') {
    return (
      <div
        className="flex items-center gap-1.5"
        title={`Provider responded in ${health.latencyMs ?? '?'}ms at ${health.lastChecked}`}
      >
        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
        <span
          data-testid="tutor-health-status"
          className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest"
        >
          Online & Ready
        </span>
      </div>
    );
  }
  const errCode = health.lastError?.code || 'UNAVAILABLE';
  const status = health.lastError?.status ?? 0;
  return (
    <div
      className="flex items-center gap-1.5"
      title={
        health.lastError
          ? `${errCode} · HTTP ${status} · ${health.lastError.internalMessage}`
          : 'AI Tutor provider is degraded'
      }
    >
      <div className="w-1.5 h-1.5 rounded-full bg-rose-500" />
      <span
        data-testid="tutor-health-status"
        className="text-[10px] font-bold text-rose-600 uppercase tracking-widest"
      >
        {errCode}
        {status ? ` · ${status}` : ''}
      </span>
    </div>
  );
}

function ErrorBubble({
  details,
  onRetry,
  canRetry,
}: {
  details: TutorErrorDetails;
  onRetry?: () => void;
  canRetry: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      data-testid="tutor-error-bubble"
      data-tutor-error-code={details.code}
      data-tutor-error-status={String(details.status)}
      data-tutor-request-id={details.requestId}
      className="flex flex-col gap-2 rounded-2xl rounded-tl-none border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-xs font-medium leading-relaxed text-rose-100"
    >
      <div className="flex items-start gap-2 text-rose-200">
        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
        <span data-testid="tutor-error-user-message" className="font-bold">
          {details.userMessage}
        </span>
      </div>

      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] font-bold uppercase tracking-widest text-rose-200/80">
        <span data-testid="tutor-error-code">code: {details.code}</span>
        <span data-testid="tutor-error-status">status: {details.status}</span>
        <span data-testid="tutor-error-request-id">req: {details.requestId.slice(0, 12)}</span>
        <span>attempt: {details.attempt}/{details.maxAttempts}</span>
      </div>

      <div className="flex items-center gap-3 pt-1">
        {canRetry && onRetry && (
          <button
            type="button"
            onClick={onRetry}
            data-testid="tutor-btn-retry"
            className="flex items-center gap-1.5 rounded-lg border border-rose-400/40 bg-rose-500/20 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-rose-100 hover:bg-rose-500/30 transition-colors"
          >
            <RotateCcw size={12} /> Retry
          </button>
        )}
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid="tutor-btn-toggle-details"
          className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-rose-200 hover:text-rose-100"
          aria-expanded={expanded}
        >
          {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          {expanded ? 'Hide details' : 'Show details'}
        </button>
      </div>

      {expanded && (
        <pre
          data-testid="tutor-error-details"
          className="mt-2 max-h-48 overflow-auto rounded-lg border border-rose-400/30 bg-black/30 p-3 text-[10px] leading-relaxed text-rose-100 whitespace-pre-wrap break-words"
        >{JSON.stringify(details, null, 2)}</pre>
      )}
    </div>
  );
}

// SafeAssistantMarkdown — renders assistant message text through react-markdown
// with raw HTML disabled (react-markdown v10's default) and a tight set of
// allowed elements styled for the dark tutor panel. We never use
// dangerouslySetInnerHTML; react-markdown escapes any embedded HTML so model
// output cannot inject scripts even if it tried.
function SafeAssistantMarkdown({ content }: { content: string }) {
  return (
    <div
      data-testid="tutor-assistant-markdown"
      className="prose prose-invert max-w-none text-xs leading-relaxed"
    >
      <ReactMarkdown
        components={{
          p: ({ children }) => (
            <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>
          ),
          strong: ({ children }) => (
            <strong className="font-bold text-emerald-300">{children}</strong>
          ),
          em: ({ children }) => <em className="italic">{children}</em>,
          ul: ({ children }) => (
            <ul className="list-disc pl-5 my-2 space-y-1">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal pl-5 my-2 space-y-1">{children}</ol>
          ),
          li: ({ children }) => <li className="leading-relaxed">{children}</li>,
          code: ({ children }) => (
            <code className="px-1 py-0.5 rounded bg-black/30 border border-white/10 text-[11px] font-mono">
              {children}
            </code>
          ),
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-emerald-400 underline hover:text-emerald-300"
            >
              {children}
            </a>
          ),
          h1: ({ children }) => (
            <h1 className="text-sm font-black mt-2 mb-1">{children}</h1>
          ),
          h2: ({ children }) => (
            <h2 className="text-sm font-bold mt-2 mb-1">{children}</h2>
          ),
          h3: ({ children }) => (
            <h3 className="text-xs font-bold mt-2 mb-1">{children}</h3>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

export const AITutor: React.FC<AITutorProps> = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      role: 'assistant',
      content:
        "Hello! I'm your Adversyn AI Trading Tutor. I can answer questions " +
        'about the Adversyn platform (Council Debate, Market Pulse, Strategy ' +
        'Lab, Simulation Lab, Portfolio, Risk Center, paper vs. live trading) ' +
        'and general trading concepts. How can I help?',
    },
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [health, setHealth] = useState<TutorHealth | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const probeRanRef = useRef(false);

  // Phase 1 voice input: native browser speech-to-text only. No audio is
  // recorded, stored, or sent backend. Final transcripts are appended to
  // `input` so the user can edit before submitting. We never auto-submit.
  const speech = useSpeechRecognition({ lang: 'en-US', continuous: false, interimResults: true });
  const lastTranscriptRef = useRef('');
  const [showUnsupportedMessage, setShowUnsupportedMessage] = useState(false);

  useEffect(() => {
    const t = speech.transcript;
    if (!t) return;
    if (t === lastTranscriptRef.current) return;
    const delta = lastTranscriptRef.current && t.startsWith(lastTranscriptRef.current)
      ? t.slice(lastTranscriptRef.current.length).trim()
      : t.trim();
    lastTranscriptRef.current = t;
    if (!delta) return;
    setInput((prev) => {
      const base = prev.trimEnd();
      if (!base) return delta;
      return `${base} ${delta}`;
    });
  }, [speech.transcript]);

  useEffect(() => {
    if (speech.listening) {
      lastTranscriptRef.current = '';
    }
  }, [speech.listening]);

  const handleMicClick = useCallback(() => {
    if (!speech.supported) {
      setShowUnsupportedMessage(true);
      return;
    }
    setShowUnsupportedMessage(false);
    if (speech.listening) {
      speech.stop();
    } else {
      speech.reset();
      lastTranscriptRef.current = '';
      speech.start();
    }
  }, [speech]);

  // Auto-scroll on new message.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // On first open, probe health. Also listen for live success/error events so
  // the pill stays current with whatever the tutor is actually doing.
  useEffect(() => {
    if (!isOpen || probeRanRef.current) return;
    probeRanRef.current = true;
    probeTutorHealth().then(setHealth).catch(() => {
      setHealth({
        status: 'degraded',
        lastChecked: new Date().toISOString(),
      });
    });
  }, [isOpen]);

  useEffect(() => {
    function onErr(e: Event) {
      const detail = (e as CustomEvent<TutorErrorDetails>).detail;
      if (!detail) return;
      // Don't downgrade health on a single failure if it's MISSING_API_KEY —
      // those are persistent; everything else can flicker.
      setHealth((prev) => ({
        status: 'degraded',
        latencyMs: detail.latencyMs,
        lastChecked: detail.timestamp,
        lastError: detail,
      }));
    }
    function onOk(e: Event) {
      const detail = (e as CustomEvent<{ latencyMs: number; timestamp: string }>).detail;
      setHealth({
        status: 'ok',
        latencyMs: detail?.latencyMs,
        lastChecked: detail?.timestamp || new Date().toISOString(),
      });
    }
    window.addEventListener('adversyn:tutor-error', onErr);
    window.addEventListener('adversyn:tutor-success', onOk);
    return () => {
      window.removeEventListener('adversyn:tutor-error', onErr);
      window.removeEventListener('adversyn:tutor-success', onOk);
    };
  }, []);

  const sendPrompt = useCallback(
    async (history: ChatMessage[], promptIndex: number) => {
      setIsLoading(true);
      try {
        const text = await getTutorResponse(asTutorMessages(history));
        setMessages((prev) => [...prev, { role: 'assistant', content: text }]);
      } catch (raw: unknown) {
        // No generic fallback. Surface the actual error.
        const details: TutorErrorDetails =
          raw instanceof TutorError
            ? raw.details
            : {
                code: 'UNKNOWN_TUTOR_ERROR',
                status: 0,
                requestId: `client-${Date.now()}`,
                provider: 'unknown',
                model: 'unknown',
                endpoint: 'unknown',
                latencyMs: 0,
                attempt: 0,
                maxAttempts: 0,
                timestamp: new Date().toISOString(),
                retryable: false,
                userMessage:
                  raw instanceof Error
                    ? `AI Tutor failed: ${raw.message}`
                    : 'AI Tutor failed with an unknown error.',
                internalMessage:
                  raw instanceof Error ? raw.message : 'Non-Error thrown from tutor service.',
              };
        setMessages((prev) => [
          ...prev,
          { role: 'error', content: details.userMessage, error: details, promptIndex },
        ]);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const handleSend = useCallback(async () => {
    if (!input.trim() || isLoading) return;
    const userMessage: ChatMessage = { role: 'user', content: input };
    const next = [...messages, userMessage];
    const promptIndex = next.length - 1;
    setMessages(next);
    setInput('');
    await sendPrompt(next, promptIndex);
  }, [input, isLoading, messages, sendPrompt]);

  const handleRetry = useCallback(
    async (errorMessageIndex: number) => {
      const errorMsg = messages[errorMessageIndex];
      if (!errorMsg || errorMsg.role !== 'error') return;
      // Truncate the error message and resend the conversation up to the
      // user prompt that produced it.
      const truncated = messages.slice(0, errorMessageIndex);
      setMessages(truncated);
      await sendPrompt(truncated, errorMsg.promptIndex ?? truncated.length - 1);
    },
    [messages, sendPrompt]
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-6 bottom-24 w-[400px] h-[600px] bg-[var(--app-bg)] border border-[var(--card-header-border)] shadow-[0_20px_50px_rgba(0,0,0,0.3)] z-[70] flex flex-col rounded-2xl overflow-hidden"
          >
            <div className="p-6 border-b border-[var(--card-header-border)] bg-[var(--card-bg)] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center shadow-[0_0_15px_rgba(16,185,129,0.4)]">
                  <Sparkles
                    size={20}
                    className="text-white fill-white/20 drop-shadow-[0_0_5px_rgba(255,255,255,0.8)]"
                  />
                </div>
                <div>
                  <h3 className="text-sm font-black text-[var(--app-text)] uppercase italic tracking-tight">
                    AI Trading Tutor
                  </h3>
                  <div className="flex items-center gap-2">
                    <HealthPill health={health} />
                    <span
                      data-testid="tutor-knowledge-version"
                      title="Adversyn platform knowledge grounding loaded"
                      className="text-[9px] font-bold text-emerald-700/80 uppercase tracking-widest"
                    >
                      Grounded · {ADVERSYN_KNOWLEDGE_VERSION}
                    </span>
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-[var(--input-bg)] rounded-lg transition-colors text-[var(--muted-text)]"
              >
                <X size={20} />
              </button>
            </div>

            <div
              ref={scrollRef}
              data-testid="tutor-panel-messages"
              className="flex-1 overflow-y-auto p-6 space-y-6 scrollbar-thin scrollbar-thumb-emerald-500/20"
            >
              {messages.map((msg, i) => {
                if (msg.role === 'error' && msg.error) {
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex flex-col max-w-[90%] items-start"
                    >
                      <ErrorBubble
                        details={msg.error}
                        canRetry={!isLoading}
                        onRetry={() => handleRetry(i)}
                      />
                      <span className="text-[9px] font-bold text-rose-300 mt-1.5 uppercase tracking-widest">
                        Tutor Error · {safeFormatTimestamp(msg.error.timestamp, { mode: 'time', fallback: '—', invalidFallback: '—' })}
                      </span>
                    </motion.div>
                  );
                }
                return (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    key={i}
                    className={cn(
                      'flex flex-col max-w-[85%]',
                      msg.role === 'user' ? 'ml-auto items-end' : 'items-start'
                    )}
                  >
                    <div
                      className={cn(
                        'px-4 py-3 rounded-2xl text-xs font-medium leading-relaxed',
                        msg.role === 'user'
                          ? 'bg-emerald-600 text-white rounded-tr-none whitespace-pre-wrap'
                          : 'bg-[var(--card-bg)] border border-[var(--card-header-border)] text-[var(--app-text)] rounded-tl-none'
                      )}
                    >
                      {msg.role === 'assistant' ? (
                        <SafeAssistantMarkdown content={msg.content} />
                      ) : (
                        msg.content
                      )}
                    </div>
                    <span className="text-[9px] font-bold text-[var(--muted-text)] mt-1.5 uppercase tracking-widest">
                      {msg.role === 'user' ? 'You' : 'Adversyn AI'}
                    </span>
                  </motion.div>
                );
              })}
              {isLoading && (
                <div
                  className="flex items-center gap-3 text-emerald-500"
                  data-testid="tutor-loading"
                >
                  <Loader2 size={16} className="animate-spin" />
                  <span className="text-[10px] font-black uppercase tracking-widest italic">
                    Analyzing Markets...
                  </span>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-[var(--card-header-border)] bg-[var(--card-bg)]">
              <div
                className="text-[10px] font-bold uppercase tracking-widest mb-2 flex items-center gap-2"
                data-testid="tutor-mic-status"
                aria-live="polite"
                style={{ minHeight: '14px' }}
              >
                {speech.listening && (
                  <span
                    className="flex items-center gap-1.5 text-emerald-500"
                    data-testid="tutor-mic-listening"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    Listening...
                  </span>
                )}
                {!speech.listening && speech.errorMessage && (
                  <span
                    className="text-rose-400"
                    data-testid="tutor-mic-error"
                  >
                    {speech.errorMessage}
                  </span>
                )}
                {!speech.listening && !speech.errorMessage && showUnsupportedMessage && !speech.supported && (
                  <span
                    data-testid="tutor-voice-error"
                    data-tutor-voice-error-code="UNSUPPORTED"
                    className="block text-[10px] text-rose-400 mb-2"
                  >
                    Voice input is not supported in this browser. Please type your message instead.
                  </span>
                )}
              </div>
              <div className="relative">
                <button
                  type="button"
                  onClick={handleMicClick}
                  aria-label={
                    !speech.supported
                      ? 'Voice input not supported in this browser'
                      : speech.listening
                        ? 'Stop voice input'
                        : 'Start voice input'
                  }
                  aria-pressed={speech.listening}
                  data-testid="tutor-btn-mic"
                  data-mic-supported={speech.supported ? 'true' : 'false'}
                  data-mic-listening={speech.listening ? 'true' : 'false'}
                  data-tutor-mic-listening={speech.listening ? 'true' : 'false'}
                  disabled={!speech.supported}
                  className={cn(
                    'absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-lg transition-all',
                    speech.listening
                      ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-[0_0_15px_rgba(16,185,129,0.6)] animate-pulse'
                      : speech.errorMessage
                        ? 'bg-[var(--input-bg)] border border-rose-500/60 text-rose-400 hover:border-rose-400'
                        : speech.supported
                          ? 'bg-[var(--input-bg)] border border-[var(--input-border)] text-[var(--app-text)] hover:border-emerald-500'
                          : 'bg-[var(--input-bg)] border border-[var(--input-border)] text-[var(--muted-text)] opacity-60 cursor-help'
                  )}
                >
                  <Mic size={16} />
                </button>
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                  placeholder={
                    speech.listening
                      ? 'Listening — speak now, then review before sending...'
                      : 'Ask about strategies, risk, or market structure...'
                  }
                  data-testid="tutor-input-message"
                  className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl pl-12 pr-12 py-3 text-xs font-medium text-[var(--app-text)] placeholder:text-[var(--muted-text)] focus:border-emerald-500 outline-none transition-all"
                />
                <button
                  onClick={handleSend}
                  disabled={!input.trim() || isLoading}
                  data-testid="tutor-btn-send"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:hover:bg-emerald-600 text-white rounded-lg transition-all"
                >
                  <Send size={16} />
                </button>
              </div>
              <div className="mt-4 flex items-center justify-center gap-4">
                <div className="flex items-center gap-1.5 text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
                  <Brain size={12} />
                  Deep Reasoning
                </div>
                <div className="flex items-center gap-1.5 text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
                  <Sparkles size={12} />
                  Real-time Data
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
